import React, { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Input } from "../../atoms/Input";
import { Button } from "../../atoms/Button";
import { Select, SelectOptionType } from "../../atoms/Select";
import FormTemplate from "../../templates/FormTemplate";
import { USER_ROLE } from "../../../constants/app";


export type MemberFormData = {
  name: string;
  email: string;
  userRole: USER_ROLE;
};

type Props = {
  handleSubmit: (data: MemberFormData, onError: (message: string) => void) => void;
  defaultValues?: Partial<MemberFormData>;
  loading?: boolean;
  mode?: "create" | "edit";
};

export const MemberForm: React.FC<Props> = ({
  handleSubmit,
  defaultValues = {},
  loading = false,
  mode = "create",
}) => {
  const [hasNameError, setHasNameError] = useState<boolean>(false);
  const [hasEmailError, setHasEmailError] = useState<boolean>(false);
  const [hasUserRoleError, setHasUserRoleError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // Local submission state to prevent multiple clicks
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRoleOptions: SelectOptionType[] = useMemo(() => {
    return Object.values(USER_ROLE).map((role) => ({
      id: role,
      name: role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' '),
      value: role,
    }));
  }, []);

  const schema = useMemo(() => {
    return z.object({
      name: z.string().min(1, "Name is required"),
      email: mode === "edit"
        ? z.string().optional()
        : z.string().email("Invalid email address").min(1, "Email is required"),
      userRole: z.nativeEnum(USER_ROLE, {
        required_error: "User Role is required",
        invalid_type_error: "Invalid user role",
      }),
    });
  }, [mode]);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  const handleSelectChange = (name: string, value: any) => {
    if (methods) {
      methods.setValue(name, value);
    }
  };

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      isSubmitting={isSubmitting}
      defaultValues={{
        name: defaultValues.name || "",
        email: defaultValues.email || "",
        userRole: defaultValues.userRole || USER_ROLE.NORMAL_USER,
      }}
      onSubmit={async (data) => {
        // Prevent submission if already submitting
        if (isSubmitting) {
          return;
        }

        setIsSubmitting(true);

        try {
          // Reset errors
          setHasNameError(false);
          setHasEmailError(false);
          setHasUserRoleError(false);
          setErrorMessage(undefined);

          // Wrap handleSubmit in a Promise
          await new Promise<void>((resolve, reject) => {
            handleSubmit(data as MemberFormData, (message) => {
              setErrorMessage(message);
              reject(new Error(message));
            });

            // For successful submissions, resolve the promise
            setTimeout(resolve, 100);
          });
        } catch (error: any) {
          // Error is already handled by setting errorMessage above
          console.log('Member form submission error handled:', error.message);
          setIsSubmitting(false);
        }
        // Don't reset isSubmitting on success - let the modal close handle it
      }}
    >
      <StyledFormGrid>
        <StyledInputContainer>
          <Input
            label="Name"
            name="name"
            type="text"
            placeholder="Enter name"
            autoFocus
            required
            hasError={hasNameError}
            errorMessage={hasNameError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>

        <StyledInputContainer>
          <Input
            label="Email"
            name="email"
            type="email"
            placeholder="Enter email"
            required={mode !== "edit"}
            disabled={mode === "edit"}
            hasError={hasEmailError}
            errorMessage={hasEmailError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>

        <StyledSelectContainer>
          <Select
            label="User Role"
            name="userRole"
            options={userRoleOptions}
            placeholder="Select user role"
            onChange={handleSelectChange}
            required
            hasSearchInput={false}
            hasError={hasUserRoleError}
            errorMessage={hasUserRoleError ? errorMessage : undefined}
            width="100%"
            defaultValue={defaultValues.userRole}
          />
        </StyledSelectContainer>

      </StyledFormGrid>

      <StyledFooter>
        <Button
          variant="filled"
          text={isSubmitting ? (mode === "create" ? "Creating..." : "Updating...") : (mode === "create" ? "Create" : "Update")}
          type="submit"
          disabled={loading}
          isSubmitting={isSubmitting}
        />
      </StyledFooter>
    </FormTemplate>
  );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;
